import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import {
  applyVariantSelectionToPicker,
  pickerMatchesTargetVariant,
  resolveSmartVariant,
  isVariantInStockForSelection,
  applyOptimisticOptionAvailability,
} from '@theme/variant-availability';
import { ProductCardServices } from './product-card-services.js';
import { productCardMediaFilter } from './product-card-media-filter.js';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #lifecycleAbort;

  /** @type {boolean} */
  #isProgrammaticChange = false;

  /** @type {NewProduct | undefined} */
  #pendingMorphProduct;

  /**
   * LRU cache of Section Rendering HTML keyed by request URL.
   * Avoids re-downloading/parsing the same option_values on A→B→A taps (INP).
   * @type {Map<string, string>}
   */
  static #sectionHtmlCache = new Map();

  static #SECTION_HTML_CACHE_MAX = 8;

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /** Stable change handler — avoids stacking `.bind(this)` on every reconnect. */
  #onVariantChanged = (event) => {
    this.variantChanged(event);
  };

  connectedCallback() {
    super.connectedCallback();

    // Reconnect / morph can call connected again — tear down prior listeners first.
    this.#lifecycleAbort?.abort();
    this.#lifecycleAbort = new AbortController();
    const { signal } = this.#lifecycleAbort;

    this.addEventListener('change', this.#onVariantChanged, { signal });

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener(
        'change',
        (event) => {
          if (event.isTrusted && event.target instanceof HTMLElement) {
            this.dataset.userVariantSelection = 'true';
          }
        },
        { capture: true, signal }
      );

      form.addEventListener(
        'submit',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        },
        { signal }
      );

      // Also prevent any button clicks inside the variant picker from submitting forms
      const buttons = form.querySelectorAll('button, input[type="button"], input[type="submit"]');
      buttons.forEach((button) => {
        button.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          },
          { signal }
        );
      });

      // Prevent labels and inputs from triggering any form submission
      const labels = form.querySelectorAll('label');
      labels.forEach((label) => {
        label.addEventListener(
          'click',
          (e) => {
            e.stopPropagation();
          },
          { capture: true, signal }
        );
      });

      // Also prevent radio inputs from triggering form submission
      // BUT: Don't stop propagation for variant cards - legacy handlers need the events
      const radioInputs = form.querySelectorAll('input[type="radio"]');
      radioInputs.forEach((input) => {
        input.addEventListener(
          'click',
          (e) => {
            const isVariantCard = input.closest('[data-collection-variant-id]') !== null;
            if (!isVariantCard) {
              e.stopPropagation();
            }
          },
          { capture: true, signal }
        );
      });
    }

    // One-time smart landing on product page load only.
    requestAnimationFrame(() => {
      this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        this.#applySwatchOutline(input);
      });
      this.#applySmartVariantSelectionOnce();
    });
  }

  disconnectedCallback() {
    this.#lifecycleAbort?.abort();
    this.#lifecycleAbort = null;
    this.#abortController?.abort();
    this.#abortController = undefined;
    super.disconnectedCallback();
  }

  /**
   * PDP + quick-add drawer allow selecting sold-out sizes.
   * @param {HTMLElement} target
   * @returns {boolean}
   */
  #allowsUnavailableVariantSelection(target) {
    if (target.closest('product-card')) return false;
    if (target.closest('quick-add-dialog')) return true;
    return (
      typeof Theme !== 'undefined' &&
      typeof Theme.template?.name === 'string' &&
      (Theme.template.name === 'product' || Theme.template.name.startsWith('product.')) &&
      this.dataset.templateProductMatch === 'true'
    );
  }

  /** @returns {boolean} */
  #isInQuickAddDrawer() {
    return Boolean(this.closest('quick-add-dialog'));
  }

  /**
   * Product-like variant UX: optimistic sizes + instant media filter.
   * Includes the quick-add drawer (cloned PDP picker) — previously excluded,
   * which forced a full-document fetch on every size/swatch change.
   * @param {Event} event
   * @returns {boolean}
   */
  #usesProductLikeVariantUx(event) {
    if (this.#isInQuickAddDrawer()) return true;
    if (!(event.target instanceof Element)) return false;
    if (event.target.closest('product-card')) return false;

    return (
      (typeof Theme !== 'undefined' &&
        typeof Theme.template?.name === 'string' &&
        (Theme.template.name === 'product' || Theme.template.name.startsWith('product.'))) ||
      (typeof document !== 'undefined' &&
        typeof document.body?.dataset?.template === 'string' &&
        (document.body.dataset.template === 'product' ||
          document.body.dataset.template.startsWith('product.')))
    );
  }

  /**
   * @param {string} variantId
   */
  #syncQuickAddFormVariant(variantId) {
    const dialog = this.closest('quick-add-dialog');
    if (!dialog) return;

    dialog.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = variantId;
      input.disabled = false;
      input.removeAttribute('disabled');
    });

    // Refresh drawer ATC/sold-out state from local variants JSON (no network).
    dialog.querySelector('#quick-add-modal-content')?.dispatchEvent(
      new CustomEvent('quick-add:purchase-refresh', { bubbles: true })
    );
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio' &&
      event.target.dataset.optionAvailable === 'false'
    ) {
      const allowsUnavailableSelection = this.#allowsUnavailableVariantSelection(event.target);

      if (!allowsUnavailableSelection) {
        event.preventDefault();
        event.target.checked = false;
        event.target.removeAttribute('checked');
        event.target.closest('label')?.classList.remove('checked');

        const fieldset = event.target.closest('fieldset');
        const fallback =
          fieldset?.querySelector('input[type="radio"][data-option-available="true"]:checked') ??
          fieldset?.querySelector('input[type="radio"][data-option-available="true"]');

        if (fallback instanceof HTMLInputElement) {
          this.updateSelectedOption(fallback);
          this.#isProgrammaticChange = true;
          try {
            this.fetchUpdatedSection(this.buildRequestUrl(fallback));
          } finally {
            this.#isProgrammaticChange = false;
          }
        }
        return;
      }
    }

    if (event.isTrusted) {
      this.dataset.userVariantSelection = 'true';
    }
    
    // Check if this is a collection card swatch change
    const isCollectionCard = event.target.closest('[data-collection-variant-id]') !== null ||
                            (event.target.closest('product-card') && 
                             event.target.closest('.card-gallery') &&
                             event.target.closest('variant-picker'));
    
    // For collection cards, update URL but don't fetch/morph to prevent page blinking
    // CRITICAL: Don't call updateSelectedOption for variant cards - legacy [data-collection-variant-id] product-card handlers handles that
    if (isCollectionCard && event.target.type === 'radio' && event.target.closest('.variant-option--swatches')) {
      // Update the selected state (but skip unchecking others - legacy [data-collection-variant-id] product-card handlers handles that)
      const variantId = event.target.dataset.variantId;
      const connectedProductUrl = event.target.dataset.connectedProductUrl;
      const productCard = event.target.closest('product-card');
      
      const swatchColorValue =
        ProductCardServices.getInputMediaColorKey(event.target) ||
        event.target.value ||
        '';

      if (productCard && (variantId || connectedProductUrl)) {
        // Update the product card link URL
        const productLink = productCard.querySelector('a.product-card__link') || 
                           productCard.querySelector('a[href*="/products/"]');
        
        if (productLink) {
          if (connectedProductUrl) {
            // Use connected product URL if available
            productLink.href = connectedProductUrl;
          } else if (variantId) {
            // Build URL with variant parameter
            const baseUrl = productLink.href.split('?')[0];
            productLink.href = `${baseUrl}?variant=${variantId}`;
          }
        }
      }

      if (productCard && swatchColorValue) {
        const colorFieldset = event.target.closest('fieldset.variant-option--swatches');
        if (colorFieldset instanceof HTMLElement) {
          colorFieldset.dataset.userHasManuallySelected = 'true';
        }

        const card = event.target.closest('.product-grid__item[data-collection-variant-id]');
        if (card instanceof HTMLElement) {
          card.dataset.collectionVariantColor = swatchColorValue;
        }
        productCard.setAttribute('data-collection-variant-color', swatchColorValue);
        productCard.setAttribute('data-variant-color', swatchColorValue);
        productCard.setAttribute('data-variant-color-name', swatchColorValue);
        const cardGallery = productCard.querySelector('.card-gallery');
        const quickAdd = productCard.querySelector('quick-add-component');
        // Collection variant cards: legacy [data-collection-variant-id] product-card handlers already applies color on click/change.
        // Only filter here for non-variant collection product cards.
        if (cardGallery instanceof HTMLElement && !card) {
          cardGallery.setAttribute('data-variant-color', swatchColorValue);
          ProductCardServices.setActiveFilterColor(cardGallery, swatchColorValue);
          cardGallery.removeAttribute('data-gallery-server-filtered');
          productCardMediaFilter.applyColor(cardGallery, swatchColorValue);
        } else if (cardGallery instanceof HTMLElement) {
          cardGallery.setAttribute('data-variant-color', swatchColorValue);
        }
        if (quickAdd && typeof quickAdd.clearCache === 'function') {
          quickAdd.clearCache();
        }
        if (variantId) {
          productCard.setAttribute('data-variant-id', variantId);
          const variantInput = productCard.querySelector('quick-add-component input[name="id"]');
          if (variantInput instanceof HTMLInputElement) {
            variantInput.value = variantId;
          }
        }

        productCard.dispatchEvent(
          new CustomEvent('variant:update', {
            bubbles: false,
            cancelable: true,
            detail: { data: { colorValue: swatchColorValue } },
          })
        );
      }
      
      // Dispatch variant selected event for other components
      this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));
      
      // Don't fetch/morph - card-gallery handles media filtering smoothly
      // Don't call updateSelectedOption - legacy [data-collection-variant-id] product-card handlers handles swatch selection
      // Don't stop propagation - let legacy [data-collection-variant-id] product-card handlers handle the event
      return;
    }
    
    // Prevent event from bubbling to parent forms (but allow change event to work normally)
    // BUT: Don't stop propagation for variant cards - legacy [data-collection-variant-id] product-card handlers needs the events
    const isVariantCard = event.target.closest('[data-collection-variant-id]') !== null;
    if (!isVariantCard) {
      event.stopPropagation();
    }

    // Update selected state immediately for instant feedback (optimistic update)
    // BUT: Skip for variant cards - legacy [data-collection-variant-id] product-card handlers handles that
    if (!isVariantCard) {
      this.updateSelectedOption(event.target);
    }

    const isInQuickAddDrawer = this.#isInQuickAddDrawer();
    const useProductLikeUx = this.#usesProductLikeVariantUx(event);

    // PDP + quick-add drawer: update size availability + auto-land a valid size for the new color
    // from local variants JSON before the section fetch — avoids stale sizes /
    // blank Notify CTA while morph is in flight.
    if (useProductLikeUx && !isVariantCard && event.target instanceof HTMLElement) {
      const optimistic = applyOptimisticOptionAvailability(this, event.target);
      if (optimistic.sizeChanged && optimistic.control) {
        this.updateSelectedOption(optimistic.control);
      }
      if (optimistic.variant?.id) {
        const id = String(optimistic.variant.id);
        if (event.target instanceof HTMLInputElement) {
          event.target.dataset.variantId = id;
        }
        if (isInQuickAddDrawer) {
          this.#syncQuickAddFormVariant(id);
        } else {
          this.#syncVariantUrl(id);
        }
      }
      // Ensure legends reflect color + any auto-landed size.
      this.#updateOptionValueLabels();
    }

    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // For product cards (including recommendation cards), dispatch immediate variant:update event with colorValue
    // This enables instant media filtering before the fetch completes
    const isProductCard = event.target.closest('product-card') !== null;
    if (isProductCard && !isVariantCard && event.target.type === 'radio' && event.target.closest('.variant-option--swatches')) {
      const colorValue = event.target.value || '';
      const colorFieldset = event.target.closest('fieldset.variant-option--swatches');
      if (colorFieldset instanceof HTMLElement) {
        colorFieldset.dataset.userHasManuallySelected = 'true';
      }
      if (colorValue) {
        // Dispatch immediate variant:update event with colorValue for instant media filtering
        const immediateUpdateEvent = new CustomEvent('variant:update', {
          bubbles: true,
          cancelable: true,
          detail: {
            resource: null, // Will be updated after fetch
            sourceId: this.selectedOptionId || '',
            data: {
              colorValue: colorValue,
              productId: this.dataset.productId ?? '',
              mediaOnly: true,
            }
          }
        });
        this.dispatchEvent(immediateUpdateEvent);
      }
    }

    // PDP + quick-add drawer color swatches: filter gallery by alt-text media color key (not display label).
    if (
      useProductLikeUx &&
      !isVariantCard &&
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio' &&
      (event.target.closest('.variant-option--swatches') ||
        event.target.hasAttribute('data-media-color-key'))
    ) {
      const colorValue =
        ProductCardServices.getInputMediaColorKey(event.target) ||
        event.target.value ||
        '';
      if (colorValue) {
        this.dispatchEvent(
          new CustomEvent('variant:update', {
            bubbles: true,
            cancelable: true,
            detail: {
              resource: null,
              sourceId: this.selectedOptionId || '',
              data: {
                colorValue,
                productId: this.dataset.productId ?? '',
                mediaOnly: true,
              },
            },
          })
        );
      }
    }

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    // Combined-listing full morph only on real PDP — never rewrite the collection URL from the drawer.
    const loadsNewProduct = useProductLikeUx && !isInQuickAddDrawer && !!newUrl && newUrl !== currentUrl;

    // Fetch updated section in background - UI already updated optimistically.
    // Same-product PDP uses section_id (small HTML). Combined listing swaps need full document for <main> morph.
    this.fetchUpdatedSection(
      this.buildRequestUrl(event.target, null, [], { fullDocument: loadsNewProduct }),
      loadsNewProduct
    );

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Only sync browser URL on the real PDP — not while browsing a collection with the drawer open.
    if (useProductLikeUx && !isInQuickAddDrawer) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (!isInQuickAddDrawer && url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      // CRITICAL: Skip collection variant cards - legacy [data-collection-variant-id] product-card handlers handles this
      const isCollectionCard = target.closest('[data-collection-variant-id]') !== null;
      if (isCollectionCard) {
        // Don't interfere with collection card swatch selection at all
        // Just ensure the target is checked (legacy [data-collection-variant-id] product-card handlers handles unchecking others)
        if (!target.checked) {
          target.checked = true;
        }
        return;
      }
      
      // Uncheck other inputs in the same fieldset
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        const otherInputs = fieldset.querySelectorAll('input[type="radio"]');
        otherInputs.forEach(input => {
          if (input !== target) {
            input.checked = false;
            input.removeAttribute('checked');
            input.closest('label')?.classList.remove('checked');
            // Remove outline from unchecked swatches
            this.#removeSwatchOutline(input);
          }
        });
      }
      target.checked = true;
      target.setAttribute('checked', 'checked');
      target.closest('label')?.classList.add('checked');
      // Apply outline to checked swatch
      requestAnimationFrame(() => {
        this.#applySwatchOutline(target);
      });
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }

    // Same-product path skips picker morph — keep COLOR:/SIZE: legends in sync locally.
    this.#updateOptionValueLabels();
  }

  /**
   * Refresh legend labels (COLOR: X / SIZE: Y) from the current checked controls.
   * Previously these only updated when the picker was morphed from the section fetch.
   */
  #updateOptionValueLabels() {
    this.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const labelEl = fieldset.querySelector('.variant-option__swatch-value');
      if (!(labelEl instanceof HTMLElement)) return;

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.value) {
        // Prefer visible label text when present (aria-label / sibling text), else value.
        const aria = (checked.getAttribute('aria-label') || '').trim();
        let label = (aria || checked.value).trim();
        const colorMap = window.pzColorDisplayLabels || {};
        const sizeMap = window.pzSizeDisplayLabels || {};
        const lower = label.toLowerCase();
        const handle = lower.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const mapped = colorMap[lower] || colorMap[handle] || sizeMap[lower];
        if (mapped) label = String(mapped);
        labelEl.textContent = label.toUpperCase();
        return;
      }

      const select = fieldset.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value) {
        const opt = select.options[select.selectedIndex];
        let text = (opt?.textContent || select.value).replace(/\s*-\s*.+$/, '').trim();
        const colorMap = window.pzColorDisplayLabels || {};
        const sizeMap = window.pzSizeDisplayLabels || {};
        const lower = text.toLowerCase();
        const handle = lower.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const mapped = colorMap[lower] || colorMap[handle] || sizeMap[lower];
        if (mapped) text = String(mapped);
        labelEl.textContent = text.toUpperCase();
      }
    });

    this.querySelectorAll('.variant-option--dropdowns').forEach((wrap) => {
      const labelEl = wrap.querySelector('.variant-option__swatch-value');
      const select = wrap.querySelector('select');
      if (!(labelEl instanceof HTMLElement) || !(select instanceof HTMLSelectElement) || !select.value) {
        return;
      }
      const opt = select.options[select.selectedIndex];
      let text = (opt?.textContent || select.value).replace(/\s*-\s*.+$/, '').trim();
      const colorMap = window.pzColorDisplayLabels || {};
      const sizeMap = window.pzSizeDisplayLabels || {};
      const lower = text.toLowerCase();
      const handle = lower.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const mapped = colorMap[lower] || colorMap[handle] || sizeMap[lower];
      if (mapped) text = String(mapped);
      labelEl.textContent = text.toUpperCase();
    });
  }

  /**
   * Apply outline to checked swatch
   * Note: CSS now handles this, but we keep this for compatibility
   * We don't apply inline styles to allow CSS transitions to work smoothly
   */
  #applySwatchOutline(input) {
    // CSS handles the outline via :has(:checked) selector
    // No inline styles needed - allows smooth CSS transitions
  }

  /**
   * Remove outline from unchecked swatch
   * Note: CSS now handles this automatically
   */
  #removeSwatchOutline(input) {
    // CSS handles removing outline automatically when unchecked
    // No inline styles needed - allows smooth CSS transitions
  }

  /**
   * On product pages, land on first purchasable color + size once per page load (Option B).
   */
  #applySmartVariantSelectionOnce() {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (this.dataset.userVariantSelection === 'true') return;
    if (this.dataset.smartSelectionApplied === 'true') return;

    const configElement = this.querySelector('script[data-product-variants-config]');
    if (!configElement?.textContent) return;

    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const preferredVariantId = params.get('variant');

    const targetVariant = resolveSmartVariant({
      variants: config.variants,
      colorOptionIndex: config.colorOptionIndex,
      preferredVariantId,
      hideColorsWithoutAvailableSizes: Boolean(config.hideColorsWithoutAvailableSizes),
    });

    if (!targetVariant) {
      this.dataset.smartSelectionApplied = 'true';
      return;
    }

    const preferredIsInStock = preferredVariantId
      ? config.variants.some(
          (variant) =>
            String(variant.id) === String(preferredVariantId) && isVariantInStockForSelection(variant)
        )
      : false;

    if (preferredVariantId && String(preferredVariantId) === String(targetVariant.id) && preferredIsInStock) {
      this.#syncSmartSelectionPurchaseState(targetVariant);
      this.dataset.smartSelectionApplied = 'true';
      return;
    }

    const alreadyMatches = pickerMatchesTargetVariant(this, targetVariant);
    if (alreadyMatches) {
      applyVariantSelectionToPicker(this, targetVariant);
      this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        this.updateSelectedOption(input);
        this.#applySwatchOutline(input);
      });
      this.#syncVariantUrl(String(targetVariant.id));
      this.#syncSmartSelectionPurchaseState(targetVariant);
      this.dataset.smartSelectionApplied = 'true';
      return;
    }

    const { changed, control } = applyVariantSelectionToPicker(this, targetVariant);

    if (changed && control) {
      this.#isProgrammaticChange = true;
      try {
        this.updateSelectedOption(control);
        this.#syncVariantUrl(String(targetVariant.id));
        this.fetchUpdatedSection(this.buildRequestUrl(control));
      } finally {
        this.#isProgrammaticChange = false;
      }
    } else {
      this.#syncSmartSelectionPurchaseState(targetVariant);
    }

    this.dataset.smartSelectionApplied = 'true';
  }

  /**
   * Sync buy buttons / accelerated checkout after smart landing selection.
   * @param {Record<string, unknown>} targetVariant
   */
  #syncSmartSelectionPurchaseState(targetVariant) {
    const control =
      this.querySelector('input[type="radio"]:checked[data-option-available="true"]') ??
      this.querySelector('input[type="radio"]:checked') ??
      this.querySelector('select');

    if (!(control instanceof HTMLElement)) {
      this.dispatchEvent(
        new VariantUpdateEvent(targetVariant, this.selectedOptionId ?? '', {
          productId: this.dataset.productId ?? '',
        })
      );
      return;
    }

    this.#isProgrammaticChange = true;
    try {
      this.fetchUpdatedSection(this.buildRequestUrl(control));
    } finally {
      this.#isProgrammaticChange = false;
    }
  }

  /**
   * @param {string} variantId
   */
  #syncVariantUrl(variantId) {
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === variantId) return;
    url.searchParams.set('variant', variantId);
    history.replaceState({}, '', url.toString());
  }

  /**
   * Section Rendering API id for same-product fetches.
   * Prefers data-section-id; falls back to closest shopify-section wrapper.
   * @returns {string}
   */
  #resolveSectionId() {
    const fromData = (this.dataset.sectionId || '').trim();
    if (fromData) return fromData;

    const section = this.closest('.shopify-section');
    const id = section?.id || '';
    if (id.startsWith('shopify-section-')) {
      return id.slice('shopify-section-'.length);
    }
    return '';
  }

  /**
   * @param {string} requestUrl
   * @returns {string | null}
   */
  #getCachedSectionHtml(requestUrl) {
    const cached = VariantPicker.#sectionHtmlCache.get(requestUrl);
    if (cached == null) return null;
    // Refresh LRU order
    VariantPicker.#sectionHtmlCache.delete(requestUrl);
    VariantPicker.#sectionHtmlCache.set(requestUrl, cached);
    return cached;
  }

  /**
   * @param {string} requestUrl
   * @param {string} responseText
   */
  #setCachedSectionHtml(requestUrl, responseText) {
    if (!requestUrl || !responseText) return;
    if (VariantPicker.#sectionHtmlCache.has(requestUrl)) {
      VariantPicker.#sectionHtmlCache.delete(requestUrl);
    }
    VariantPicker.#sectionHtmlCache.set(requestUrl, responseText);
    while (VariantPicker.#sectionHtmlCache.size > VariantPicker.#SECTION_HTML_CACHE_MAX) {
      const oldest = VariantPicker.#sectionHtmlCache.keys().next().value;
      if (oldest == null) break;
      VariantPicker.#sectionHtmlCache.delete(oldest);
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {{ fullDocument?: boolean }} [options] - When fullDocument is true, skip section_id (combined listing <main> morph).
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], options = {}) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // When selectedOption is a select, get connectedProductUrl from the selected option element.
    const optionEl = selectedOption instanceof HTMLSelectElement
      ? selectedOption.options[selectedOption.selectedIndex]
      : selectedOption;
    let productUrl = optionEl?.dataset?.connectedProductUrl || selectedOption.dataset?.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // Get all selected options
    const allSelectedOptions = this.selectedOptionsValues;

    if (allSelectedOptions.length && !source) {
      params.push(`option_values=${allSelectedOptions.join(',')}`);
    } else if (source === 'product-card') {
      if (allSelectedOptions.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    } else {
      // Fallback: if no selected options found, use the selected option's ID
      params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    // Quick-add drawer: fetch only the product section — never the full PDP document.
    if (this.closest('quick-add-dialog')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      const sectionId =
        document.getElementById('quick-add-dialog')?.getAttribute('data-product-section-id')?.trim() ||
        'main';
      return `${productUrl}?section_id=${encodeURIComponent(sectionId)}&${params.join('&')}`;
    }

    // PDP same-product changes: Section Rendering API only (not full ~700KB document).
    // Combined-listing product identity changes pass fullDocument: true for <main> morph.
    if (!options.fullDocument) {
      const sectionId = this.#resolveSectionId();
      if (sectionId) {
        if (productUrl?.includes('?')) {
          productUrl = productUrl.split('?')[0];
        }
        return `${productUrl}?section_id=${encodeURIComponent(sectionId)}&${params.join('&')}`;
      }
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * Same-product changes skip full picker morph (optimistic local size UI already applied)
   * and only emit VariantUpdateEvent with HTML for ATC/price/inventory.
   * Combined listings (product identity change) still morph the picker.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    const canUseCache = !shouldMorphMain && !window.Shopify?.designMode;
    const cachedHtml = canUseCache ? this.#getCachedSectionHtml(requestUrl) : null;

    const applyResponseText = (responseText) => {
      if (signal.aborted) return;
      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      const selectedVariantScript = html.querySelector(
        'variant-picker script[type="application/json"]:not([data-product-variants-config])'
      );
      const textContent =
        selectedVariantScript?.textContent ||
        html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!textContent) {
        return;
      }

      if (shouldMorphMain) {
        this.updateMain(html);
        return;
      }

      let variantData;
      try {
        variantData = JSON.parse(textContent);
      } catch {
        return;
      }
      const newVariantPickerSource = html.querySelector(this.tagName.toLowerCase());
      /** @type {NewProduct | undefined} */
      let newProduct;

      if (newVariantPickerSource instanceof HTMLElement) {
        const newProductId = newVariantPickerSource.dataset.productId;
        const newProductUrl = newVariantPickerSource.dataset.productUrl;

        if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
          newProduct = { id: newProductId, url: newProductUrl };
          // Combined listing product change — must morph picker + related UI.
          this.updateVariantPicker(html, () => {
            this.#emitVariantUpdate(html, variantData, newProduct);
          });
          return;
        }

        this.dataset.productId = newProductId;
        this.dataset.productUrl = newProductUrl;

        // Patch variants config + selected-variant JSON without replacing picker DOM.
        const newConfig = newVariantPickerSource.querySelector('script[data-product-variants-config]');
        const oldConfig = this.querySelector('script[data-product-variants-config]');
        if (newConfig?.textContent && oldConfig) {
          oldConfig.textContent = newConfig.textContent;
        }
        const newSelectedVariantScript = newVariantPickerSource.querySelector(
          'script[type="application/json"]:not([data-product-variants-config])'
        );
        const oldSelectedVariantScript = this.querySelector(
          'script[type="application/json"]:not([data-product-variants-config])'
        );
        if (newSelectedVariantScript?.textContent && oldSelectedVariantScript) {
          oldSelectedVariantScript.textContent = newSelectedVariantScript.textContent;
        }

        // Refresh option availability from local JSON (no morph flash).
        applyOptimisticOptionAvailability(this, this.selectedOption);
        this.#updateOptionValueLabels();
      }

      this.#emitVariantUpdate(html, variantData, newProduct);
    };

    if (cachedHtml) {
      applyResponseText(cachedHtml);
      return;
    }

    fetch(requestUrl, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (canUseCache) this.#setCachedSectionHtml(requestUrl, responseText);
        applyResponseText(responseText);
      })
      .catch((error) => {
        // Silently handle aborted requests (user-initiated)
        if (error.name !== 'AbortError') {
          // Error occurred but don't log to console in production
        }
      });
  }

  /**
   * @param {Document} html
   * @param {object} variantData
   * @param {NewProduct | undefined} [newProduct]
   */
  #emitVariantUpdate(html, variantData, newProduct) {
    // Prefer the selected-variant script (patched after fetch). Config JSON has no .id.
    let syncedVariant = this.#getSelectedVariantJson();
    if (!syncedVariant?.id && variantData?.id) {
      syncedVariant = variantData;
    }
    if (!syncedVariant?.id) {
      // Fallback: resolve from local variants config + current radios.
      const configEl = this.querySelector('script[data-product-variants-config]');
      try {
        const config = configEl?.textContent ? JSON.parse(configEl.textContent) : null;
        const variants = config?.variants;
        if (Array.isArray(variants)) {
          const values = [];
          this.querySelectorAll('fieldset').forEach((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            if (checked?.value) values.push(String(checked.value));
          });
          this.querySelectorAll('select').forEach((select) => {
            if (select.value) values.push(String(select.value));
          });
          syncedVariant =
            variants.find((v) => {
              const options = Array.isArray(v.options)
                ? v.options.map(String)
                : [v.option1, v.option2, v.option3].filter(Boolean).map(String);
              return (
                options.length === values.length &&
                options.every((opt, i) => String(opt) === String(values[i]))
              );
            }) || null;
        }
      } catch {
        /* ignore */
      }
    }

    let colorValue = '';
    const selectedSwatch = this.querySelector(
      '.variant-option--swatches input[type="radio"]:checked'
    );
    if (selectedSwatch) {
      colorValue =
        ProductCardServices.getInputMediaColorKey(selectedSwatch) ||
        selectedSwatch.value ||
        '';
    }

    if (!this.selectedOptionId) return;

    const eventData = {
      html,
      productId: this.dataset.productId ?? '',
      newProduct,
    };

    if (colorValue) {
      eventData.colorValue = colorValue;
    }

    this.dispatchEvent(
      new VariantUpdateEvent(syncedVariant || variantData, this.selectedOptionId, eventData)
    );
  }

  /**
   * Re-renders the variant picker (combined listings / product identity change).
   * @param {Document} newHtml - The new HTML.
   * @param {(() => void) | undefined} [onSynced] - Called after morph and selection restore.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml, onSynced) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    const savedSelection = this.selectedOptionsValues;
    const shouldPreserveSelection =
      this.dataset.userVariantSelection === 'true' && savedSelection.length > 0;

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    this.#pendingMorphProduct = newProduct;

    queueMicrotask(() => {
      morph(this, newVariantPickerSource);
      requestAnimationFrame(() => {
        if (shouldPreserveSelection) {
          this.#restorePickerSelection(savedSelection);
        }
        this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          this.#applySwatchOutline(input);
        });
        onSynced?.();
      });
    });

    return newProduct;
  }

  /**
   * Reads the selected variant JSON embedded in the picker after a section fetch.
   * @returns {Record<string, unknown> | null}
   */
  #getSelectedVariantJson() {
    const script = this.querySelector(
      'script[type="application/json"]:not([data-product-variants-config])'
    );
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Re-apply picker UI after morph so user selections are not overwritten by server HTML.
   * @param {string[]} optionValueIds
   */
  #restorePickerSelection(optionValueIds) {
    optionValueIds.forEach((optionValueId) => {
      const control = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (control instanceof HTMLInputElement || control instanceof HTMLOptionElement) {
        this.updateSelectedOption(control);
      }
    });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type {Element} */
    let root = this;
    // Quick-add modal: only read from the visible block (desktop or mobile) to avoid mixing both
    if (this.classList.contains('variant-picker--quick-add')) {
      const desktopOnly = this.querySelector('.variant-picker__desktop-only');
      const mobileOnly = this.querySelector('.variant-picker__mobile-only');
      if (desktopOnly && mobileOnly) {
        const isMobile = window.matchMedia('(max-width: 1024px)').matches;
        root = isMobile ? mobileOnly : desktopOnly;
      }
    }
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(root.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
